import { Button, Label, Spinner, Textarea } from 'flowbite-react';
import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import Select, { SingleValue } from 'react-select';
import { Dropdown } from 'shared-utils';
import { moveToNextRound } from 'src/apis/application';
import { useToast } from 'src/context/ToastProvider';
import { ShortListCandidateInput } from 'types';

interface Props {
	userId?: string;
	userName?: string;
	currentRound: number;
	totalRounds: number;
	positionId: string;
	isLoading?: boolean;
	onSubmit?: (data: ShortListCandidateInput) => void;
	onFinish?: () => void;
	nextRoundLabel?: string;
	showMessageInput?: boolean;
}

const RecruiterCandidateMoveToNextRound = ({
	userId,
	userName,
	onSubmit,
	onFinish,
	isLoading,
	positionId,
	currentRound,
	totalRounds,
	nextRoundLabel,
	showMessageInput = true,
}: Props) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const [round, setRound] = React.useState<number>();
	const [feedback, setFeedback] = React.useState('');

	const moveUserToNextRoundFn = useMutation(['moveToNextRound'], moveToNextRound, {
		onSuccess(data) {
			queryClient.refetchQueries({ queryKey: ['getRecommendationsForPosition'], exact: false });
			toast({
				title: data.nextRoundExists
					? `${userName} moved to ${currentRound ?? 0 + 1}`
					: `${userName} successfully qualified all rounds`,
				type: 'success',
			});
			setRound(undefined);
			onFinish && onFinish();
		},
		onError(error: Error) {
			toast({
				title: `Error occured ${error.message}`,
				type: 'error',
			});
		},
	});

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		console.log('{ positionId, userId: userId ??, message: feedback }:', {
			positionId,
			userId: userId ?? '',
			message: feedback,
		});
		if (onSubmit) onSubmit({ positionId, userId: userId ?? '' });
		else moveUserToNextRoundFn.mutate({ positionId, userId: userId ?? '', message: feedback });
	};

	const nextRoundExists = currentRound < totalRounds;
	const isLastRound = currentRound === totalRounds;
	const nextRoundString =
		nextRoundLabel ||
		(currentRound != undefined
			? isLastRound
				? 'Qualified All Rounds'
				: `Round ${currentRound + 1}`
			: 'Move to next round');

	return (
		<form onSubmit={handleSubmit}>
			<div className="pt-3">
				<div className="mb-[8px]">
					<Label>{'Select Stage '}</Label>
					<span className="text-[red]">*</span>
				</div>

				<Select
					options={[{ label: nextRoundString, value: `${currentRound + 1}` }]}
					menuPlacement={'auto'}
					classNamePrefix="my-select-plans"
					className="single-value-select-tag"
					onChange={(val: SingleValue<Dropdown>) => setRound(Number(val?.value || 0))}
					placeholder={nextRoundString}
				/>

				{/* Recruiter Feedback */}
				{showMessageInput ? (
					<div className="mb-[8px] mt-[16px]">
						<div className="mb-[8px] flex justify-between items-end">
							<div>
								<Label htmlFor="message" value="Feedback" />
							</div>

							<span className="italic text-xs text-[#6B7280] ">{(feedback?.length || 0) + ' / ' + 640}</span>
						</div>
						<Textarea
							style={{
								fontFamily: "'Inter', sans-serif",
								borderRadius: '8px',
							}}
							className="min-h-[87px]"
							value={feedback}
							maxLength={640}
							id="message"
							name="message"
							placeholder="Write your message here..."
							onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFeedback(e.target.value)}
						/>
					</div>
				) : null}
			</div>

			<Button
				type="submit"
				className="w-full mt-[20px] bg-[#7777E7]"
				color="purplePlaced"
				disabled={!round || moveUserToNextRoundFn.isLoading || isLoading}
			>
				{moveUserToNextRoundFn.isLoading || isLoading ? (
					<Spinner />
				) : nextRoundExists ? (
					`Move to Round ${currentRound + 1}`
				) : (
					'Move to next round'
				)}
			</Button>
		</form>
	);
};

export default RecruiterCandidateMoveToNextRound;
